import {
    Dispatch,
    RefObject,
    SetStateAction,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';

import { useSelector } from 'react-redux';

import { selectPlayerConfig } from 'store/player/selectors';

import { VideoItem } from 'services/data-manager/types.ts';
import useGetPlaybackResumeUavParams from 'services/uav/useGetPlaybackResumeUavParams.ts';
import useGetPlaybackSkipAheadUavParams from 'services/uav/useGetPlaybackSkipAheadUavParams.ts';
import { logUavAnalytics } from 'services/uav/utils.ts';

import useUpdateEffect from 'utils/hooks/useUpdateEffect.ts';
import isNumber from 'utils/number.ts';

import { PlayerRef } from 'components/Player/types.ts';

import { OnSkipHandler, SKIP_STEP } from './useSkippings.ts';
import {
    PlayerControlsAccessLevel,
    accessIsRestricted,
} from './useTrickModeControl.ts';

type Params = {
    playerRef: RefObject<PlayerRef | null>;
    videoItem: VideoItem | undefined;
    isLive: boolean;
    isCatchup: boolean;
    isStartover: boolean;
    fastforwardAccessLevel: PlayerControlsAccessLevel;
    videoDuration: number;
    playerProgress: number;
    seeking: boolean;
    playerPausedByUser: boolean;
    templateId?: string;
    railId?: string;
    setPlayerProgress: Dispatch<SetStateAction<number>>;
    handleSkip: OnSkipHandler;
    onShowControlsRestrictedNotification(): void;
    handleJumpToLive(): void;
};

const SKIP_DEBOUNCE_TIME = 400;

function useSkipForward({
    playerRef,
    videoItem,
    isLive,
    isCatchup,
    isStartover,
    fastforwardAccessLevel,
    videoDuration,
    playerProgress,
    seeking,
    playerPausedByUser,
    templateId,
    railId,
    setPlayerProgress,
    handleSkip,
    onShowControlsRestrictedNotification,
    handleJumpToLive,
}: Params) {
    const { playbackType } = useSelector(selectPlayerConfig);
    const { getPlaybackResumeUavParams } = useGetPlaybackResumeUavParams({
        playbackType,
        videoItem,
    });
    const { getPlaybackSkipAheadUavParams } = useGetPlaybackSkipAheadUavParams({
        playbackType,
        videoItem,
    });

    const skipInProgress = useRef(false);
    const skipFwdAttempts = useRef(0);
    const skipFwdTimeout = useRef<NodeJS.Timeout | null>(null);

    const [logEvent, setLogEvent] = useState(false);

    useUpdateEffect(() => {
        if (!seeking && skipInProgress.current && !playerPausedByUser) {
            playerRef.current?.play();
            skipInProgress.current = false;

            if (skipFwdTimeout.current != null) {
                clearTimeout(skipFwdTimeout.current);
                skipFwdTimeout.current = null;
            }
        }
    }, [seeking]);

    useEffect(() => {
        if (logEvent) {
            logUavAnalytics(
                getPlaybackResumeUavParams({
                    templateId,
                    railId,
                }),
            );
        }

        setLogEvent(false);
    }, [logEvent]);

    const handleSkipForwardDynamicStartover = useCallback(async () => {
        const maxTimeShift = await playerRef.current?.getMaxTimeShift();

        if (isNumber(maxTimeShift)) {
            playerRef.current?.pause();
            skipInProgress.current = true;
            skipFwdAttempts.current += 1;
            setPlayerProgress((prevState) =>
                Math.min(Math.abs(maxTimeShift), prevState + SKIP_STEP),
            );

            if (skipFwdTimeout.current != null) {
                clearTimeout(skipFwdTimeout.current);
                skipFwdTimeout.current = null;
            }

            skipFwdTimeout.current = setTimeout(async () => {
                const currentTimeShift =
                    await playerRef.current?.getTimeShift();

                if (isNumber(currentTimeShift)) {
                    const target = Math.min(
                        0,
                        currentTimeShift + skipFwdAttempts.current * SKIP_STEP,
                    );

                    if (target === 0) {
                        handleJumpToLive();

                        if (playerPausedByUser) {
                            setTimeout(() => {
                                setLogEvent(true);
                            }, 500);
                        }
                    } else {
                        handleSkip({
                            timeShiftOffset: target,
                        });
                    }
                }

                skipFwdAttempts.current = 0;
            }, SKIP_DEBOUNCE_TIME);
        }
    }, [
        playerRef,
        setPlayerProgress,
        getPlaybackResumeUavParams,
        templateId,
        railId,
        handleJumpToLive,
        handleSkip,
    ]);

    const handleSkipForwardStaticStream = useCallback(() => {
        playerRef.current?.pause();
        skipInProgress.current = true;
        setPlayerProgress((prevState) => {
            return Math.min(videoDuration, prevState + SKIP_STEP);
        });

        if (skipFwdTimeout.current != null) {
            clearTimeout(skipFwdTimeout.current);
            skipFwdTimeout.current = null;
        }

        skipFwdTimeout.current = setTimeout(() => {
            handleSkip({
                seekPosition: Math.min(
                    videoDuration,
                    playerProgress + SKIP_STEP,
                ),
            });
        }, SKIP_DEBOUNCE_TIME);
    }, [
        handleSkip,
        playerProgress,
        playerRef,
        setPlayerProgress,
        videoDuration,
    ]);

    return useCallback(async () => {
        if (!seeking) {
            if (accessIsRestricted(fastforwardAccessLevel)) {
                onShowControlsRestrictedNotification();
            } else {
                const isDynamicStream =
                    (isStartover || isCatchup) &&
                    (await playerRef.current?.getIsLive());

                logUavAnalytics(
                    getPlaybackSkipAheadUavParams({
                        startPosition: playerProgress,
                        endPosition: playerProgress + SKIP_STEP,
                    }),
                );

                if (isDynamicStream) {
                    handleSkipForwardDynamicStartover();
                } else if (!isLive) {
                    handleSkipForwardStaticStream();
                }
            }
        }
    }, [
        seeking,
        fastforwardAccessLevel,
        onShowControlsRestrictedNotification,
        isStartover,
        isCatchup,
        playerRef,
        getPlaybackSkipAheadUavParams,
        playerProgress,
        isLive,
        handleSkipForwardDynamicStartover,
        handleSkipForwardStaticStream,
    ]);
}

export default useSkipForward;
